import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { PegaState } from "../journey/pega/apiGetPegaState";
import { subscribeToPCoreMessage } from "../utils/pCore";

type PegaAppContext = {
  Product?: string;
  Platform?: string;
  CustomerType?: string;
  Context?: string;
  IsFromPlatform?: boolean;
  Channel?: string;
  MultiAsset?: string;
  IsCancelled?: boolean | string;
  ForceNoModal?: boolean | string;
  SumsubVerificationLink?: string;
};

interface PegaEventContextValue {
  initPegaSession: () => void;
  subscribeToPegaEvents: () => void;
  unsubscribeFromPegaEvents: () => void;
  isEndOfAssignment: boolean;
  resetEndOfAssignment: () => void;
  resetAppContext: () => void;
  onPegaTokenExpiry: () => void;
  pegaState?: PegaState;
  setPegaState: (state: PegaState | undefined) => void;
  appContext: PegaAppContext;
}

const PegaEventContext = createContext<PegaEventContextValue | undefined>(
  undefined,
);

type PegaEventProviderProps = {
  onPegaTokenExpiry: () => void;
  children: ReactNode;
};

type UnsubscribeFn = () => void;

const logSetAppContext = (message: string, payload?: unknown) => {
  if (!import.meta.env.DEV && import.meta.env.VITE_CUSTOM_ENV !== 'nonprod') {
    return;
  }

  console.info(`[CMC_CRM_SET_APP_CONTEXT] ${new Date().toISOString()} ${message}`, payload);
};

const waitForPCore = (): Promise<unknown> => {
  return new Promise((resolve, reject) => {
    const maxAttempts = 100;
    let attempts = 0;

    const checkIfPCoreIsAvailable = () => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const PCore = (window as any).PCore;

      if (PCore) {
        resolve(PCore);
        return;
      }

      attempts++;
      if (attempts >= maxAttempts) {
        reject(new Error("PCore not available after 10 seconds"));
        return;
      }

      setTimeout(checkIfPCoreIsAvailable, 100);
    };

    checkIfPCoreIsAvailable();
  });
};

const subscribeToCustomReauth = async (
  onPegaTokenExpiry: () => void,
): Promise<UnsubscribeFn | undefined> => {
  if (typeof window === "undefined") return;

  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const PCore = (await waitForPCore()) as any;

    PCore.getPubSubUtils().subscribe(
      window.PCore.getConstants().PUB_SUB_EVENTS.EVENT_CUSTOM_REAUTH,
      () => {
        console.info("Pega token expired, redirecting back to login");
        onPegaTokenExpiry();
      },
      "customReauthSubscriptionKey",
    );

    return () => {
      PCore.getPubSubUtils().unsubscribe(
        PCore.getConstants().PUB_SUB_EVENTS.EVENT_CUSTOM_REAUTH,
        "customReauthSubscriptionKey",
      );
    };
  } catch (error) {
    console.error("Failed to subscribe to Pega customReauth event:", error);
    return;
  }
};

const SET_APP_CONTEXT_MATCHER = "CMC_CRM_SET_APP_CONTEXT";
const SET_APP_CONTEXT_SUBSCRIPTION_ID = "onboardingGatewaySetAppContext";

let isSetAppContextActive = false;
let setAppContextUnsubscribe: UnsubscribeFn | undefined;

const teardownSetAppContextSubscription = () => {
  isSetAppContextActive = false;

  if (!setAppContextUnsubscribe) {
    return;
  }

  const unsubscribe = setAppContextUnsubscribe;
  setAppContextUnsubscribe = undefined;
  logSetAppContext("unsubscribing");
  unsubscribe();
};

const subscribeToSetAppContext = async (
  onAppContext: (context: PegaAppContext) => void,
): Promise<UnsubscribeFn | undefined> => {
  if (typeof window === "undefined") return;

  try {
    teardownSetAppContextSubscription();
    await waitForPCore();

    isSetAppContextActive = true;

    const subscription = subscribeToPCoreMessage<PegaAppContext>(
      SET_APP_CONTEXT_MATCHER,
      (event) => {
        if (!isSetAppContextActive) {
          logSetAppContext("ignored (inactive)", event.message);
          return;
        }

        logSetAppContext("received", event.message);

        const {
          Product,
          Platform,
          CustomerType,
          Context,
          IsFromPlatform,
          Channel,
          MultiAsset,
          IsCancelled,
          ForceNoModal,
          SumsubVerificationLink,
        } = event.message || {};

        if (
          Product ||
          Platform ||
          CustomerType ||
          Context ||
          IsFromPlatform ||
          Channel ||
          MultiAsset ||
          IsCancelled !== undefined ||
          ForceNoModal !== undefined ||
          SumsubVerificationLink
        ) {
          const context = {
            ...(Product && { Product }),
            ...(Platform && { Platform }),
            ...(CustomerType && { CustomerType }),
            ...(Context && { Context }),
            ...(IsFromPlatform && { IsFromPlatform }),
            ...(Channel && { Channel }),
            ...(MultiAsset && { MultiAsset }),
            ...(IsCancelled !== undefined && { IsCancelled }),
            ...(ForceNoModal !== undefined && { ForceNoModal }),
            ...(SumsubVerificationLink && { SumsubVerificationLink }),
          };
          logSetAppContext("applying", context);
          onAppContext(context);
        }
      },
      undefined,
      SET_APP_CONTEXT_SUBSCRIPTION_ID,
    );

    if (!subscription) {
      isSetAppContextActive = false;
      return;
    }

    logSetAppContext("subscribed");

    setAppContextUnsubscribe = () => {
      isSetAppContextActive = false;
      subscription.unsubscribe();
      setAppContextUnsubscribe = undefined;
    };

    return setAppContextUnsubscribe;
  } catch (error) {
    isSetAppContextActive = false;
    console.error(
      "Failed to subscribe to Pega CMC_CRM_SET_APP_CONTEXT message:",
      error,
    );
    return;
  }
};

const subscribeToEndOfAssignment = async (
  onEndOfAssignment: () => void,
): Promise<UnsubscribeFn | undefined> => {
  if (typeof window === "undefined") return;

  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const PCore = (await waitForPCore()) as any;

    PCore.getPubSubUtils().subscribe(
      "endOfAssignmentProcessing",
      () => {
        onEndOfAssignment();
      },
      "endOfAssignmentProcessingSubscriptionKey",
    );

    return () => {
      PCore.getPubSubUtils().unsubscribe(
        "endOfAssignmentProcessing",
        "endOfAssignmentProcessingSubscriptionKey",
      );
    };
  } catch (error) {
    console.error(
      "Failed to subscribe to Pega endOfAssignmentProcessing event:",
      error,
    );
    return;
  }
};

export const PegaEventProvider = ({
  onPegaTokenExpiry,
  children,
}: PegaEventProviderProps) => {
  const [isEndOfAssignment, setIsEndOfAssignment] = useState(false);
  const [pegaState, setPegaState] = useState<PegaState | undefined>();
  const [appContext, setAppContext] = useState<PegaAppContext>({});
  const unsubscribePegaEventFns = useRef<UnsubscribeFn[]>([]);
  const subscriptionGenerationRef = useRef(0);
  const unsubscribeFromPegaEventsRef = useRef<() => void>(() => {});

  const resetEndOfAssignment = useCallback(() => {
    setIsEndOfAssignment(false);
  }, []);

  const resetAppContext = useCallback(() => {
    setAppContext({});
  }, []);

  const unsubscribeFromPegaEvents = useCallback(() => {
    subscriptionGenerationRef.current += 1;
    teardownSetAppContextSubscription();
    unsubscribePegaEventFns.current.forEach((fn) => {
      fn();
    });
    unsubscribePegaEventFns.current = [];
  }, []);

  useEffect(() => {
    unsubscribeFromPegaEventsRef.current = unsubscribeFromPegaEvents;
  }, [unsubscribeFromPegaEvents]);

  const initPegaSession = useCallback(() => {
    if (window.PCore) {
      // Horrible hack we have to do to get pega to load after a token expiry, without it pega just hangs.
      // This is the solution provided to us by Labbs.
      // It's extra awful having to put it in a timeout else it's very flaky.
      // Hopefully the token expiring is an edge case though.
      setTimeout(() => {
        window.PCore?.getPubSubUtils().publish("updateSession", {
          updateStatus: "success",
        });
      }, 500);
    }
  }, []);

  const subscribeToPegaEvents = useCallback(() => {
    if (unsubscribePegaEventFns.current.length > 0) {
      return;
    }

    const generation = subscriptionGenerationRef.current;

    const registerUnsubscribe = (unsubscribe: UnsubscribeFn | undefined) => {
      if (!unsubscribe) {
        return;
      }

      if (generation !== subscriptionGenerationRef.current) {
        unsubscribe();
        return;
      }

      unsubscribePegaEventFns.current.push(unsubscribe);
    };

    // Subscribe to 'customReauth' - this is so we can detect if the pega token expires
    subscribeToCustomReauth(onPegaTokenExpiry).then(registerUnsubscribe);

    // Subscribe to 'endOfAssignmentProcessing' - this is so we can show our own 'ThankYou' screen at the end
    subscribeToEndOfAssignment(() => {
      console.info("[endOfAssignmentProcessing] fired");
      setIsEndOfAssignment(true);
      // Delay teardown to allow any final SET_APP_CONTEXT messages (e.g. SumsubVerificationLink) to arrive
      setTimeout(() => {
        teardownSetAppContextSubscription();
        unsubscribeFromPegaEventsRef.current();
      }, 2000);
    }).then(registerUnsubscribe);

    // Subscribe to app context updates from Pega (Product, Platform, CustomerType)
    subscribeToSetAppContext((context) => {
      setAppContext((prev) => ({ ...prev, ...context }));
    }).then(registerUnsubscribe);
  }, [onPegaTokenExpiry]);

  useEffect(() => {
    return () => {
      unsubscribeFromPegaEvents();
    };
  }, [unsubscribeFromPegaEvents]);

  const contextValue = useMemo(
    (): PegaEventContextValue => ({
      initPegaSession,
      subscribeToPegaEvents,
      unsubscribeFromPegaEvents,
      isEndOfAssignment,
      resetEndOfAssignment,
      resetAppContext,
      onPegaTokenExpiry,
      pegaState,
      setPegaState,
      appContext,
    }),
    [isEndOfAssignment, resetEndOfAssignment, resetAppContext, initPegaSession, subscribeToPegaEvents, unsubscribeFromPegaEvents, pegaState, onPegaTokenExpiry, appContext],
  );

  return (
    <PegaEventContext.Provider value={contextValue}>
      {children}
    </PegaEventContext.Provider>
  );
};

export const usePegaEventsContext = () => {
  const context = useContext(PegaEventContext);
  if (context === undefined) {
    throw new Error("usePegaEvents must be used within PegaEventListener");
  }
  return context;
};
